import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Search, UserPlus, UserCheck, Filter, Phone, Mail, Calendar, RefreshCw, AlertTriangle, BookOpen, Hash, CheckCheck, Copy, CheckCircle, Edit } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface AcademicProgram { id: number; name: string; code: string; }
interface AcademicGroup { id: number; name: string; code: string; programId?: number; inscriptionProductId?: number | null; inscriptionProduct?: { name: string; price: string } | null; }

interface CodigoPago {
  codigoPago: string;
  sku: string;
  conceptoNombre: string;
  referencia: string;
  fuente: 'factura' | 'grupo';
}

interface CodigosPagoData {
  alumno: { id: number; nui: string; nuiDigits: string; nombre: string; };
  codigos: CodigoPago[];
}

interface Prospecto {
  id: number;
  numeroIdentificacionUnico: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string | null;
  email: string | null;
  telefono: string | null;
  programId: number | null;
  groupId: number | null;
  comoConocio: string | null;
  fechaCreacion: string;
  estatus: string;
}

interface SuccessData {
  nui: string;
  nombre: string;
  apellidoPaterno: string;
  alumnoId: number;
}

interface EnrollmentFormEntry {
  programId: number | null;
  groupId: number | null;
  isPrimary: boolean;
  status: string;
}

const emptyEnrollment: EnrollmentFormEntry = { programId: null, groupId: null, isPrimary: true, status: 'activo' };

const emptyForm = {
  nombre: '',
  apellidoPaterno: '',
  apellidoMaterno: '',
  email: '',
  telefono: '',
  programId: null as number | null,
  groupId: null as number | null,
  comoConocio: '',
  enrollments: [{ ...emptyEnrollment }] as EnrollmentFormEntry[],
};

export default function Prospectos() {
  const { user } = useAuth();
  const canManage = user?.role === 'Admin' || (user?.permissions?.includes('alumnos:create') ?? false);
  const canChangeStatus = user?.role === 'Admin' || (user?.permissions?.includes('alumnos:manage-status') ?? false);

  const [prospectos, setProspectos] = useState<Prospecto[]>([]);
  const [programs, setPrograms] = useState<AcademicProgram[]>([]);
  const [allGroups, setAllGroups] = useState<AcademicGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [programFilter, setProgramFilter] = useState<string>('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [newProspecto, setNewProspecto] = useState({ ...emptyForm });
  const [showEditForm, setShowEditForm] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editingProspecto, setEditingProspecto] = useState<Prospecto | null>(null);
  const [editProspecto, setEditProspecto] = useState({ ...emptyForm });
  const [originalGroupIds, setOriginalGroupIds] = useState<Set<number>>(new Set());
  const [showPaymentConfirmDialog, setShowPaymentConfirmDialog] = useState(false);
  const [paymentPreviews, setPaymentPreviews] = useState<Array<{
    groupId: number;
    groupName: string;
    inscriptionFee: { productId: number; productName: string; price: string; taxRate: string } | null;
    activeTuitionPlans: Array<{ id: number; name: string; installmentCount: number; totalAmount: string; firstDueDate: string }>;
  }>>([]);
  const [paymentPreviewLoading, setPaymentPreviewLoading] = useState(false);
  const [pendingEditPayload, setPendingEditPayload] = useState<Record<string, unknown> | null>(null);
  const [convertingId, setConvertingId] = useState<number | null>(null);
  const [convertReason, setConvertReason] = useState('');
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [selectedForConvert, setSelectedForConvert] = useState<Prospecto | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [successData, setSuccessData] = useState<SuccessData | null>(null);

  // Estados para advertencia de posible duplicado al crear prospecto
  type DuplicateCandidate = {
    id: number; nombre: string; apellidoPaterno: string; apellidoMaterno: string;
    email: string; telefono: string; estatus: string; numeroIdentificacionUnico: string;
  };
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateCandidates, setDuplicateCandidates] = useState<DuplicateCandidate[]>([]);
  const [pendingCreateBody, setPendingCreateBody] = useState<Record<string, unknown> | null>(null);

  // Códigos de pago
  const [showCodigosPago, setShowCodigosPago] = useState(false);
  const [codigosPagoData, setCodigosPagoData] = useState<CodigosPagoData | null>(null);
  const [loadingCodigos, setLoadingCodigos] = useState(false);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [copiedNui, setCopiedNui] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const wasSearchFocused = useRef(false);

  const loadPrograms = useCallback(async () => {
    try {
      const res = await fetch('/api/academic-programs', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPrograms(data.filter((p: any) => p.isActive));
      }
    } catch {}
  }, []);

  const loadGroups = useCallback(async () => {
    try {
      const res = await fetch('/api/academic-periods', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setAllGroups(Array.isArray(data) ? data : []);
      }
    } catch {}
  }, []);

  const loadProspectos = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      params.set('estatus', 'prospecto');
      params.set('page', String(currentPage));
      params.set('limit', '20');
      if (searchTerm.trim()) params.set('search', searchTerm.trim());

      const res = await fetch(`/api/alumnos?${params.toString()}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Error al cargar prospectos');
      const data = await res.json();

      let list: Prospecto[] = data.alumnos || [];
      if (programFilter) {
        list = list.filter((p) => String(p.programId) === programFilter);
      }
      setProspectos(list);
      setTotalPages(data.pagination?.totalPages || 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar prospectos');
    } finally {
      setLoading(false);
      if (wasSearchFocused.current) {
        requestAnimationFrame(() => {
          if (wasSearchFocused.current && searchInputRef.current) {
            searchInputRef.current.focus();
          }
        });
      }
    }
  }, [currentPage, searchTerm, programFilter]);

  useEffect(() => { loadPrograms(); loadGroups(); }, [loadPrograms, loadGroups]);
  useEffect(() => { loadProspectos(); }, [loadProspectos]);

  useEffect(() => {
    const t = setTimeout(() => { if (currentPage !== 1) setCurrentPage(1); else loadProspectos(); }, 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const handleOpenEdit = async (p: Prospecto) => {
    setEditingProspecto(p);
    setError(null);
    try {
      const res = await fetch(`/api/alumnos/${p.id}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Error al cargar datos del prospecto');
      const fullData = await res.json();

      const loadedEnrollments: EnrollmentFormEntry[] = fullData.enrollments && fullData.enrollments.length > 0
        ? fullData.enrollments.map((e: any) => ({
            programId: e.programId,
            groupId: e.groupId,
            isPrimary: e.isPrimary,
            status: e.status || 'activo',
          }))
        : fullData.programId
          ? [{ programId: fullData.programId, groupId: fullData.groupId || null, isPrimary: true, status: 'activo' }]
          : [{ ...emptyEnrollment }];

      const ogIds = new Set<number>(loadedEnrollments.map((e) => e.groupId).filter((id): id is number => !!id));
      setOriginalGroupIds(ogIds);

      setEditProspecto({
        nombre: fullData.nombre,
        apellidoPaterno: fullData.apellidoPaterno,
        apellidoMaterno: fullData.apellidoMaterno ?? '',
        email: fullData.email ?? '',
        telefono: fullData.telefono ?? '',
        programId: fullData.programId,
        groupId: fullData.groupId,
        comoConocio: fullData.comoConocio ?? '',
        enrollments: loadedEnrollments,
      });
      setShowEditForm(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar datos del prospecto');
    }
  };

  const performSaveEdit = async (body: Record<string, unknown>, generatePayments: boolean) => {
    if (!editingProspecto) return;
    try {
      setEditLoading(true);
      setError(null);

      const res = await fetch(`/api/alumnos/${editingProspecto.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...body, generatePayments }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al actualizar prospecto');
      }

      setShowEditForm(false);
      setEditingProspecto(null);
      setShowPaymentConfirmDialog(false);
      setPendingEditPayload(null);
      loadProspectos();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al actualizar prospecto');
    } finally {
      setEditLoading(false);
    }
  };

  const handleEdit = async () => {
    if (!editingProspecto) return;
    if (!editProspecto.nombre.trim() || !editProspecto.apellidoPaterno.trim()) {
      setError('Nombre y apellido paterno son requeridos');
      return;
    }
    setError(null);

    const validEnrollments = editProspecto.enrollments
      .filter((e) => e.programId)
      .map((e) => ({ programId: e.programId!, groupId: e.groupId, isPrimary: e.isPrimary, status: e.status }));

    const body: Record<string, unknown> = {
      nombre: editProspecto.nombre.trim(),
      apellidoPaterno: editProspecto.apellidoPaterno.trim(),
      apellidoMaterno: editProspecto.apellidoMaterno.trim() || null,
      email: editProspecto.email.trim() || null,
      telefono: editProspecto.telefono.trim() || null,
      comoConocio: editProspecto.comoConocio.trim() || null,
    };
    if (validEnrollments.length > 0) {
      body.enrollments = validEnrollments;
    }

    const newGroupIds = validEnrollments
      .map((e) => e.groupId)
      .filter((gId): gId is number => !!gId && !originalGroupIds.has(gId));

    if (newGroupIds.length > 0) {
      setPaymentPreviewLoading(true);
      try {
        const previews: typeof paymentPreviews = [];
        for (const gId of newGroupIds) {
          const r = await fetch(`/api/alumnos/enrollment-preview?groupId=${gId}`, { credentials: 'include' });
          if (r.ok) {
            const data = await r.json();
            const groupName = allGroups.find((g) => g.id === gId)?.name || `Grupo #${gId}`;
            if (data.inscriptionFee || (data.activeTuitionPlans && data.activeTuitionPlans.length > 0)) {
              previews.push({ groupId: gId, groupName, inscriptionFee: data.inscriptionFee, activeTuitionPlans: data.activeTuitionPlans });
            }
          }
        }
        if (previews.length > 0) {
          setPaymentPreviews(previews);
          setPendingEditPayload(body);
          setShowPaymentConfirmDialog(true);
          return;
        }
      } finally {
        setPaymentPreviewLoading(false);
      }
    }

    await performSaveEdit(body, false);
  };

  // Ejecuta la creación real (después de confirmar que no hay duplicados, o de que el usuario decidió continuar)
  const performCreateProspecto = async (body: Record<string, unknown>) => {
    try {
      setCreateLoading(true);
      setError(null);

      const res = await fetch('/api/alumnos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al crear prospecto');
      }
      const responseData = await res.json();
      const alumno = responseData.alumno;

      setShowDuplicateWarning(false);
      setPendingCreateBody(null);
      setShowCreateForm(false);
      setNewProspecto({ ...emptyForm });
      setSuccessData({
        nui: alumno.numeroIdentificacionUnico,
        nombre: alumno.nombre,
        apellidoPaterno: alumno.apellidoPaterno,
        alumnoId: alumno.id,
      });
      loadProspectos();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al crear prospecto');
    } finally {
      setCreateLoading(false);
    }
  };

  // Verifica posibles duplicados y luego crea el prospecto
  const handleCreate = async () => {
    if (!newProspecto.nombre.trim() || !newProspecto.apellidoPaterno.trim()) {
      setError('Nombre y apellido paterno son requeridos');
      return;
    }
    setError(null);

    const validEnrollments = newProspecto.enrollments
      .filter((e) => e.programId)
      .map((e) => ({ programId: e.programId!, groupId: e.groupId, isPrimary: e.isPrimary, status: e.status }));

    const body: Record<string, unknown> = {
      nombre: newProspecto.nombre.trim(),
      apellidoPaterno: newProspecto.apellidoPaterno.trim(),
      estatus: 'prospecto',
    };
    if (newProspecto.apellidoMaterno.trim()) body.apellidoMaterno = newProspecto.apellidoMaterno.trim();
    if (newProspecto.email.trim()) body.email = newProspecto.email.trim();
    if (newProspecto.telefono.trim()) body.telefono = newProspecto.telefono.trim();
    if (validEnrollments.length > 0) {
      body.enrollments = validEnrollments;
      body.programId = (validEnrollments.find((e) => e.isPrimary) || validEnrollments[0]).programId;
      body.groupId = (validEnrollments.find((e) => e.isPrimary) || validEnrollments[0]).groupId;
    }
    if (newProspecto.comoConocio.trim()) body.comoConocio = newProspecto.comoConocio.trim();

    // Verificar posibles duplicados antes de guardar
    try {
      const params = new URLSearchParams();
      params.set('nombre', newProspecto.nombre.trim());
      params.set('apellidoPaterno', newProspecto.apellidoPaterno.trim());
      if (newProspecto.email.trim()) params.set('email', newProspecto.email.trim());

      const dupRes = await fetch(`/api/alumnos/check-duplicate?${params.toString()}`, { credentials: 'include' });
      if (dupRes.ok) {
        const { duplicates } = await dupRes.json();
        if (duplicates && duplicates.length > 0) {
          setDuplicateCandidates(duplicates);
          setPendingCreateBody(body);
          setShowDuplicateWarning(true);
          return;
        }
      }
    } catch {
      // Si falla la verificación, continuar con la creación de todas formas
    }

    await performCreateProspecto(body);
  };

  const handleOpenCodigosPago = async (p: Prospecto) => {
    setLoadingCodigos(true);
    setCodigosPagoData(null);
    setShowCodigosPago(true);
    try {
      const res = await fetch(`/api/alumnos/${p.id}/codigos-pago`, { credentials: 'include' });
      if (!res.ok) throw new Error('Error al obtener códigos');
      const data: CodigosPagoData = await res.json();
      setCodigosPagoData(data);
    } catch {
      setCodigosPagoData(null);
    } finally {
      setLoadingCodigos(false);
    }
  };

  const handleCopyCode = (code: string) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    });
  };

  const handleCopyNui = (nui: string) => {
    navigator.clipboard.writeText(nui).then(() => {
      setCopiedNui(true);
      setTimeout(() => setCopiedNui(false), 2000);
    });
  };

  const openConvertModal = (p: Prospecto) => {
    setSelectedForConvert(p);
    setConvertReason('');
    setShowConvertModal(true);
  };

  const handleConvertToAspirante = async () => {
    if (!selectedForConvert) return;
    try {
      setConvertingId(selectedForConvert.id);
      const res = await fetch(`/api/alumnos/${selectedForConvert.id}/estatus`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ estatus: 'aspirante', reason: convertReason || 'Conversión manual desde módulo Prospectos' }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Error al convertir');
      }
      setShowConvertModal(false);
      setSelectedForConvert(null);
      loadProspectos();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al convertir prospecto');
    } finally {
      setConvertingId(null);
    }
  };

  const getProgramName = (programId: number | null) =>
    programId ? (programs.find((p) => p.id === programId)?.name ?? `#${programId}`) : '—';

  const getGroupName = (groupId: number | null) =>
    groupId ? (allGroups.find((g) => g.id === groupId)?.name ?? `#${groupId}`) : null;

  const formatDate = (dateStr: string) => {
    try { return new Date(dateStr).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return dateStr; }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-semibold">Prospectos</h1>
          <p className="text-gray-600">Personas interesadas en un programa con referencia de pago generada</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadProspectos} className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" />
            Actualizar
          </Button>
          {canManage && (
            <Button onClick={() => setShowCreateForm(true)} className="flex items-center gap-2">
              <UserPlus className="w-4 h-4" />
              Nuevo Prospecto
            </Button>
          )}
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <strong>¿Qué es un Prospecto?</strong> Persona interesada en un programa a la que se le generó una referencia de pago.
          No aparece en la lista general de Alumnos. Usa el botón <strong>#</strong> para ver y copiar su código de pago.
          Al convertirse en <strong>Aspirante</strong>, su comprobante de pago queda en espera de validación.
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-700">{error}</div>
      )}

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="w-4 h-4" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                ref={searchInputRef}
                placeholder="Buscar por nombre, NUI, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => { wasSearchFocused.current = true; }}
                onBlur={() => { wasSearchFocused.current = false; }}
                className="pl-10"
              />
            </div>
            <div className="w-full sm:w-64">
              <SearchableSelect
                options={programs.map((p) => ({ value: String(p.id), label: p.name }))}
                value={programFilter || null}
                onChange={(val) => setProgramFilter(val ? String(val) : '')}
                placeholder="Todos los programas"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Prospectos</CardTitle>
          <CardDescription>
            {loading ? 'Cargando...' : `${prospectos.length} prospecto${prospectos.length !== 1 ? 's' : ''} — Página ${currentPage} de ${totalPages}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-gray-500">Cargando prospectos...</div>
          ) : prospectos.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <UserPlus className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No hay prospectos registrados</p>
              {canManage && (
                <Button variant="outline" className="mt-4" onClick={() => setShowCreateForm(true)}>
                  Agregar el primero
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[860px]">
                <thead>
                  <tr className="border-b text-sm text-gray-500">
                    <th className="text-left py-3 px-4 font-medium">Acciones</th>
                    <th className="text-left py-3 px-4 font-medium">NUI</th>
                    <th className="text-left py-3 px-4 font-medium">Nombre</th>
                    <th className="text-left py-3 px-4 font-medium">Contacto</th>
                    <th className="text-left py-3 px-4 font-medium">Programa / Grupo</th>
                    <th className="text-left py-3 px-4 font-medium">Cómo Conoció</th>
                    <th className="text-left py-3 px-4 font-medium">Fecha Registro</th>
                  </tr>
                </thead>
                <tbody>
                  {prospectos.map((p) => (
                    <tr key={p.id} className="border-b hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          {canManage && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenEdit(p)}
                              title="Editar"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenCodigosPago(p)}
                            title="Códigos de pago"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                          >
                            <Hash className="w-4 h-4" />
                          </Button>
                          {canChangeStatus && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openConvertModal(p)}
                              disabled={convertingId === p.id}
                              className="flex items-center gap-1 text-green-700 border-green-300 hover:bg-green-50 text-xs"
                            >
                              <UserCheck className="w-3 h-3" />
                              Aspirante
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                          {p.numeroIdentificacionUnico}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-medium">
                        {p.apellidoPaterno} {p.apellidoMaterno ?? ''}, {p.nombre}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {p.email && (
                          <div className="flex items-center gap-1 text-gray-600">
                            <Mail className="w-3 h-3" /> {p.email}
                          </div>
                        )}
                        {p.telefono && (
                          <div className="flex items-center gap-1 text-gray-600 mt-0.5">
                            <Phone className="w-3 h-3" /> {p.telefono}
                          </div>
                        )}
                        {!p.email && !p.telefono && <span className="text-gray-400">—</span>}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-700">
                        <div>{getProgramName(p.programId)}</div>
                        {getGroupName(p.groupId) && (
                          <div className="flex items-center gap-1 text-xs text-indigo-600 mt-0.5">
                            <BookOpen className="w-3 h-3" />
                            {getGroupName(p.groupId)}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        {p.comoConocio ?? '—'}
                      </td>
                      <td className="py-3 px-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(p.fechaCreacion)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                Anterior
              </Button>
              <span className="flex items-center text-sm px-3">Pág. {currentPage} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                Siguiente
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal crear prospecto */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">Nuevo Prospecto</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nombre(s) *</label>
                  <Input
                    value={newProspecto.nombre}
                    onChange={(e) => setNewProspecto({ ...newProspecto, nombre: e.target.value })}
                    placeholder="Nombre(s)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Apellido Paterno *</label>
                  <Input
                    value={newProspecto.apellidoPaterno}
                    onChange={(e) => setNewProspecto({ ...newProspecto, apellidoPaterno: e.target.value })}
                    placeholder="Apellido paterno"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Apellido Materno</label>
                <Input
                  value={newProspecto.apellidoMaterno}
                  onChange={(e) => setNewProspecto({ ...newProspecto, apellidoMaterno: e.target.value })}
                  placeholder="Apellido materno (opcional)"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Teléfono</label>
                  <Input
                    value={newProspecto.telefono}
                    onChange={(e) => setNewProspecto({ ...newProspecto, telefono: e.target.value })}
                    placeholder="10 dígitos"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <Input
                    type="email"
                    value={newProspecto.email}
                    onChange={(e) => setNewProspecto({ ...newProspecto, email: e.target.value })}
                    placeholder="correo@ejemplo.com"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium">Programas / Grupos de Interés</label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setNewProspecto({
                      ...newProspecto,
                      enrollments: [...newProspecto.enrollments, { programId: null, groupId: null, isPrimary: false, status: 'activo' }]
                    })}
                  >
                    + Agregar Inscripción
                  </Button>
                </div>
                {newProspecto.enrollments.map((enrollment, idx) => {
                  const filteredGroupsForCard = enrollment.programId
                    ? allGroups.filter((g) => g.programId === enrollment.programId)
                    : allGroups;
                  const selectedGroupForCard = allGroups.find((g) => g.id === enrollment.groupId);
                  const hintForCard = selectedGroupForCard?.inscriptionProduct
                    ? `Concepto del grupo: ${selectedGroupForCard.inscriptionProduct.name} ($${parseFloat(selectedGroupForCard.inscriptionProduct.price).toFixed(2)})`
                    : selectedGroupForCard
                    ? 'Este grupo usará el concepto de inscripción configurado globalmente en Finanzas'
                    : null;
                  return (
                    <div key={idx} className="border rounded-lg p-3 bg-gray-50 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500">
                          Inscripción {idx + 1} {enrollment.isPrimary && <span className="text-blue-600 ml-1">(Principal)</span>}
                        </span>
                        <div className="flex gap-2">
                          {!enrollment.isPrimary && (
                            <button
                              type="button"
                              className="text-xs text-blue-600 hover:underline"
                              onClick={() => {
                                const updated = newProspecto.enrollments.map((e, i) => ({ ...e, isPrimary: i === idx }));
                                setNewProspecto({ ...newProspecto, enrollments: updated });
                              }}
                            >
                              Hacer principal
                            </button>
                          )}
                          {newProspecto.enrollments.length > 1 && (
                            <button
                              type="button"
                              className="text-xs text-red-600 hover:underline"
                              onClick={() => {
                                const updated = newProspecto.enrollments.filter((_, i) => i !== idx);
                                if (enrollment.isPrimary && updated.length > 0) updated[0].isPrimary = true;
                                setNewProspecto({ ...newProspecto, enrollments: updated });
                              }}
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">Programa</label>
                          <SearchableSelect
                            options={programs.map((p) => ({ value: p.id, label: p.name }))}
                            value={enrollment.programId}
                            onChange={(val) => {
                              const updated = [...newProspecto.enrollments];
                              updated[idx] = { ...updated[idx], programId: val ? Number(val) : null, groupId: null };
                              setNewProspecto({ ...newProspecto, enrollments: updated });
                            }}
                            placeholder="Seleccionar programa..."
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">
                            Grupo
                            <span className="text-xs text-gray-400 font-normal ml-1">(determina el concepto de inscripción)</span>
                          </label>
                          <SearchableSelect
                            options={filteredGroupsForCard.map((g) => ({ value: g.id, label: `${g.code} — ${g.name}` }))}
                            value={enrollment.groupId}
                            onChange={(val) => {
                              const updated = [...newProspecto.enrollments];
                              updated[idx] = { ...updated[idx], groupId: val ? Number(val) : null };
                              setNewProspecto({ ...newProspecto, enrollments: updated });
                            }}
                            placeholder={enrollment.programId ? 'Seleccionar grupo...' : 'Selecciona primero un programa'}
                            disabled={!enrollment.programId}
                          />
                          {hintForCard && (
                            <p className="text-xs mt-1 text-indigo-600 flex items-center gap-1">
                              <BookOpen className="w-3 h-3" />
                              {hintForCard}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">¿Cómo nos conoció?</label>
                <Input
                  value={newProspecto.comoConocio}
                  onChange={(e) => setNewProspecto({ ...newProspecto, comoConocio: e.target.value })}
                  placeholder="Redes sociales, referido, publicidad..."
                />
              </div>
            </div>
            {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => { setShowCreateForm(false); setError(null); }} disabled={createLoading}>
                Cancelar
              </Button>
              <Button onClick={handleCreate} disabled={createLoading}>
                {createLoading ? 'Creando...' : 'Crear Prospecto'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal editar prospecto */}
      {showEditForm && editingProspecto && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">
              Editar Prospecto — {editingProspecto.numeroIdentificacionUnico}
            </h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nombre(s) *</label>
                  <Input
                    value={editProspecto.nombre}
                    onChange={(e) => setEditProspecto({ ...editProspecto, nombre: e.target.value })}
                    placeholder="Nombre(s)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Apellido Paterno *</label>
                  <Input
                    value={editProspecto.apellidoPaterno}
                    onChange={(e) => setEditProspecto({ ...editProspecto, apellidoPaterno: e.target.value })}
                    placeholder="Apellido paterno"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Apellido Materno</label>
                <Input
                  value={editProspecto.apellidoMaterno}
                  onChange={(e) => setEditProspecto({ ...editProspecto, apellidoMaterno: e.target.value })}
                  placeholder="Apellido materno (opcional)"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Teléfono</label>
                  <Input
                    value={editProspecto.telefono}
                    onChange={(e) => setEditProspecto({ ...editProspecto, telefono: e.target.value })}
                    placeholder="10 dígitos"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email</label>
                  <Input
                    type="email"
                    value={editProspecto.email}
                    onChange={(e) => setEditProspecto({ ...editProspecto, email: e.target.value })}
                    placeholder="correo@ejemplo.com"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium">Programas / Grupos de Interés</label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setEditProspecto({
                      ...editProspecto,
                      enrollments: [...editProspecto.enrollments, { programId: null, groupId: null, isPrimary: false, status: 'activo' }]
                    })}
                  >
                    + Agregar Inscripción
                  </Button>
                </div>
                {editProspecto.enrollments.map((enrollment, idx) => {
                  const filteredGroupsForCard = enrollment.programId
                    ? allGroups.filter((g) => g.programId === enrollment.programId)
                    : allGroups;
                  const selectedGroupForCard = allGroups.find((g) => g.id === enrollment.groupId);
                  const hintForCard = selectedGroupForCard?.inscriptionProduct
                    ? `Concepto del grupo: ${selectedGroupForCard.inscriptionProduct.name} ($${parseFloat(selectedGroupForCard.inscriptionProduct.price).toFixed(2)})`
                    : selectedGroupForCard
                    ? 'Este grupo usará el concepto de inscripción configurado globalmente en Finanzas'
                    : null;
                  return (
                    <div key={idx} className="border rounded-lg p-3 bg-gray-50 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500">
                          Inscripción {idx + 1} {enrollment.isPrimary && <span className="text-blue-600 ml-1">(Principal)</span>}
                        </span>
                        <div className="flex gap-2">
                          {!enrollment.isPrimary && (
                            <button
                              type="button"
                              className="text-xs text-blue-600 hover:underline"
                              onClick={() => {
                                const updated = editProspecto.enrollments.map((e, i) => ({ ...e, isPrimary: i === idx }));
                                setEditProspecto({ ...editProspecto, enrollments: updated });
                              }}
                            >
                              Hacer principal
                            </button>
                          )}
                          {editProspecto.enrollments.length > 1 && (
                            <button
                              type="button"
                              className="text-xs text-red-600 hover:underline"
                              onClick={() => {
                                const updated = editProspecto.enrollments.filter((_, i) => i !== idx);
                                if (enrollment.isPrimary && updated.length > 0) updated[0].isPrimary = true;
                                setEditProspecto({ ...editProspecto, enrollments: updated });
                              }}
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium mb-1">Programa</label>
                          <SearchableSelect
                            options={programs.map((p) => ({ value: p.id, label: p.name }))}
                            value={enrollment.programId}
                            onChange={(val) => {
                              const updated = [...editProspecto.enrollments];
                              updated[idx] = { ...updated[idx], programId: val ? Number(val) : null, groupId: null };
                              setEditProspecto({ ...editProspecto, enrollments: updated });
                            }}
                            placeholder="Seleccionar programa..."
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium mb-1">
                            Grupo
                            <span className="text-xs text-gray-400 font-normal ml-1">(determina el concepto de inscripción)</span>
                          </label>
                          <SearchableSelect
                            options={filteredGroupsForCard.map((g) => ({ value: g.id, label: `${g.code} — ${g.name}` }))}
                            value={enrollment.groupId}
                            onChange={(val) => {
                              const updated = [...editProspecto.enrollments];
                              updated[idx] = { ...updated[idx], groupId: val ? Number(val) : null };
                              setEditProspecto({ ...editProspecto, enrollments: updated });
                            }}
                            placeholder={enrollment.programId ? 'Seleccionar grupo...' : 'Selecciona primero un programa'}
                            disabled={!enrollment.programId}
                          />
                          {hintForCard && (
                            <p className="text-xs mt-1 text-indigo-600 flex items-center gap-1">
                              <BookOpen className="w-3 h-3" />
                              {hintForCard}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">¿Cómo nos conoció?</label>
                <Input
                  value={editProspecto.comoConocio}
                  onChange={(e) => setEditProspecto({ ...editProspecto, comoConocio: e.target.value })}
                  placeholder="Redes sociales, referido, publicidad..."
                />
              </div>
            </div>
            {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="outline"
                onClick={() => { setShowEditForm(false); setEditingProspecto(null); setError(null); }}
                disabled={editLoading}
              >
                Cancelar
              </Button>
              <Button onClick={handleEdit} disabled={editLoading || paymentPreviewLoading}>
                {paymentPreviewLoading ? 'Verificando cobros...' : editLoading ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Diálogo de confirmación de cobros — nueva(s) inscripción(es) al editar */}
      {showPaymentConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <span className="text-amber-600 text-lg">⚠</span>
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Validación de cobros — Nueva inscripción</h2>
                <p className="text-xs text-gray-500 mt-0.5">Se detectaron obligaciones financieras para el(los) grupo(s) recién agregado(s)</p>
              </div>
            </div>

            <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto">
              {paymentPreviews.map((preview) => (
                <div key={preview.groupId} className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2 border-b">
                    <p className="text-sm font-semibold text-gray-800">📚 {preview.groupName}</p>
                  </div>
                  <div className="px-4 py-3 space-y-3">
                    {preview.inscriptionFee && (
                      <div className="flex items-start gap-3">
                        <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-blue-600 text-xs font-bold">$</span>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">Cargo de inscripción</p>
                          <p className="text-xs text-gray-500">{preview.inscriptionFee.productName}</p>
                          <p className="text-sm text-blue-700 font-semibold">
                            {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(preview.inscriptionFee.price))}
                            {preview.inscriptionFee.taxRate !== '0' && (
                              <span className="text-xs font-normal text-gray-500 ml-1">+ {preview.inscriptionFee.taxRate}% IVA</span>
                            )}
                          </p>
                        </div>
                      </div>
                    )}
                    {preview.activeTuitionPlans.length > 0 && (
                      <div className="space-y-2">
                        {preview.activeTuitionPlans.map((plan) => (
                          <div key={plan.id} className="flex items-start gap-3">
                            <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-green-600 text-xs font-bold">#</span>
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-800">Plan de colegiatura</p>
                              <p className="text-xs text-gray-500">{plan.name}</p>
                              <div className="flex flex-wrap gap-2 mt-1">
                                <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-50 text-green-700 border border-green-200">
                                  {plan.installmentCount} cuotas
                                </span>
                                <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-50 text-gray-600 border border-gray-200">
                                  Total: {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(plan.totalAmount))}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                ¿Deseas generar estos cobros automáticamente al guardar la inscripción?
              </div>
            </div>

            <div className="px-6 py-4 border-t flex flex-col sm:flex-row gap-2 sm:justify-end">
              <Button variant="outline" size="sm" onClick={() => setShowPaymentConfirmDialog(false)} disabled={editLoading}>
                Cancelar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { if (pendingEditPayload) performSaveEdit(pendingEditPayload, false); }}
                disabled={editLoading}
              >
                Solo guardar sin cobros
              </Button>
              <Button
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => { if (pendingEditPayload) performSaveEdit(pendingEditPayload, true); }}
                disabled={editLoading}
              >
                {editLoading ? 'Guardando...' : 'Confirmar y generar cobros'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal éxito — prospecto creado */}
      {successData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">¡Prospecto creado!</h2>
                <p className="text-sm text-gray-500">{successData.apellidoPaterno}, {successData.nombre}</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-2">NUI asignado</p>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xl font-bold text-gray-900">{successData.nui}</span>
                <button
                  onClick={() => handleCopyNui(successData.nui)}
                  className="text-gray-400 hover:text-emerald-600 transition-colors ml-auto"
                  title="Copiar NUI"
                >
                  {copiedNui ? <CheckCheck className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <p className="text-sm text-gray-500 mb-5">
              Usa el botón <Hash className="w-3.5 h-3.5 inline text-emerald-600" /> en la lista para ver y copiar el código de pago y compartirlo con el prospecto.
            </p>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  setSuccessData(null);
                  handleOpenCodigosPago({ id: successData.alumnoId } as Prospecto);
                }}
              >
                <Hash className="w-4 h-4 mr-1.5 text-emerald-600" />
                Ver códigos de pago
              </Button>
              <Button className="flex-1" onClick={() => setSuccessData(null)}>
                Listo
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Códigos de Pago */}
      {showCodigosPago && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b">
              <div className="flex items-center gap-2">
                <Hash className="w-5 h-5 text-emerald-600" />
                <div>
                  <h2 className="font-semibold text-gray-900 text-base">Códigos de Pago</h2>
                  {codigosPagoData && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {codigosPagoData.alumno.nui} — {codigosPagoData.alumno.nombre}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => { setShowCodigosPago(false); setCodigosPagoData(null); }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <span className="text-xl leading-none">&times;</span>
              </button>
            </div>

            {/* Body */}
            <div className="p-5">
              {loadingCodigos ? (
                <div className="flex items-center justify-center py-10">
                  <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mr-3" />
                  <span className="text-sm text-gray-500">Consultando facturas activas...</span>
                </div>
              ) : !codigosPagoData ? (
                <div className="text-center py-8">
                  <p className="text-sm text-red-500">No se pudieron cargar los códigos. Intenta de nuevo.</p>
                </div>
              ) : codigosPagoData.codigos.length === 0 ? (
                <div className="text-center py-8">
                  <Hash className="w-10 h-10 text-gray-200 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 font-medium">Sin códigos de pago disponibles</p>
                  <p className="text-xs text-gray-400 mt-1">No hay facturas activas ni producto de inscripción asignado</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-gray-400 mb-3">
                    Código = 4 dígitos NUI + 3 dígitos concepto. Comparte con el prospecto para realizar su pago.
                  </p>
                  {codigosPagoData.codigos.map((item) => (
                    <div
                      key={item.sku}
                      className="flex items-center justify-between gap-3 bg-gray-50 rounded-lg px-4 py-3 border border-gray-100"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-lg font-bold tracking-widest text-gray-900">
                            {codigosPagoData.alumno.nuiDigits}
                            <span className="text-emerald-600">{item.sku}</span>
                          </span>
                          <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                            item.fuente === 'factura'
                              ? 'bg-blue-100 text-blue-600'
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {item.fuente === 'factura' ? 'Factura' : 'Grupo'}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5 truncate font-medium">{item.conceptoNombre}</p>
                        {item.referencia && (
                          <p className="text-xs text-gray-400">{item.referencia}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleCopyCode(item.codigoPago)}
                        title="Copiar código"
                        className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                          copiedCode === item.codigoPago
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-white border border-gray-200 text-gray-600 hover:border-emerald-400 hover:text-emerald-600'
                        }`}
                      >
                        {copiedCode === item.codigoPago ? (
                          <><CheckCheck className="w-3.5 h-3.5" /> Copiado</>
                        ) : (
                          <><Copy className="w-3.5 h-3.5" /> Copiar</>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t flex justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setShowCodigosPago(false); setCodigosPagoData(null); }}
              >
                Cerrar
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal convertir a aspirante */}
      {showConvertModal && selectedForConvert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-2">Convertir a Aspirante</h2>
            <p className="text-gray-600 text-sm mb-4">
              Al convertir a <strong>{selectedForConvert.nombre} {selectedForConvert.apellidoPaterno}</strong> a ASPIRANTE,
              su pago quedará en espera de validación y aparecerá en la lista de alumnos.
            </p>
            <div>
              <label className="block text-sm font-medium mb-1">Motivo (opcional)</label>
              <Input
                value={convertReason}
                onChange={(e) => setConvertReason(e.target.value)}
                placeholder="Ej. Pago comprobado presencialmente"
              />
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowConvertModal(false)} disabled={!!convertingId}>
                Cancelar
              </Button>
              <Button
                onClick={handleConvertToAspirante}
                disabled={!!convertingId}
                className="bg-green-600 hover:bg-green-700"
              >
                {convertingId ? 'Convirtiendo...' : 'Confirmar — Convertir a Aspirante'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Diálogo de aviso de posible prospecto duplicado */}
      {showDuplicateWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <span className="text-amber-600 text-xl">⚠</span>
              </div>
              <div>
                <h2 className="text-base font-semibold text-gray-900">Posible duplicado detectado</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Ya existe{duplicateCandidates.length > 1 ? 'n' : ''} {duplicateCandidates.length} registro{duplicateCandidates.length > 1 ? 's' : ''} con datos similares en el sistema
                </p>
              </div>
            </div>

            <div className="p-6 space-y-3 max-h-[50vh] overflow-y-auto">
              {duplicateCandidates.map(c => (
                <div key={c.id} className="border border-amber-200 bg-amber-50 rounded-lg px-4 py-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-gray-900">
                      {c.nombre} {c.apellidoPaterno} {c.apellidoMaterno}
                    </span>
                    <span className="text-xs text-gray-500 bg-white border border-gray-200 rounded px-1.5 py-0.5 shrink-0">
                      {c.numeroIdentificacionUnico}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-600">
                    {c.email && <span>✉ {c.email}</span>}
                    {c.telefono && <span>📞 {c.telefono}</span>}
                    <span className="capitalize">● {c.estatus}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-lg">
              <p className="text-xs text-gray-500 mb-3">
                ¿El nuevo registro es una persona diferente o deseas continuar de todas formas?
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { setShowDuplicateWarning(false); setPendingCreateBody(null); }}
                  disabled={createLoading}
                >
                  Cancelar — revisar el formulario
                </Button>
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  onClick={() => { if (pendingCreateBody) performCreateProspecto(pendingCreateBody); }}
                  disabled={createLoading}
                >
                  {createLoading ? 'Creando...' : 'Crear de todas formas'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
